import { useEffect, useState } from 'react'
import { Chart, ChartConfiguration, ScriptableContext, registerables } from 'chart.js'
import { ProfitData } from '../../types/charts'
import 'chartjs-adapter-luxon'
import { Series, SeriesOptions } from '../../utils/series'
import zoomPlugin from 'chartjs-plugin-zoom'
import { Box, Stack, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { sprintf } from 'sprintf-js'
import { ChartSummary } from './ChartSummary'
import { drawLogoWhenLoaded, LOGO_PADDING, logoPlugin } from './logoPlugin'
import { ScatterPlot } from '@mui/icons-material'
import { DateTime } from 'luxon'
import { PROFIT_BAR_COUNT } from '../../types/constants'
import { lang } from '../../utils/lang'
import { maxOf, minOf } from '../../utils/math'
import { ProfitBars } from '../../utils/profitBars'
import { Profits } from '../../utils/profits'

Chart.register(zoomPlugin, ...registerables)

export interface ProfitHistoryChartProps {
  id: string
  spinner?: boolean
  data: ProfitData[]
  options: SeriesOptions
  onClick?: () => void
  /** The reserve compensation is one figure after the service fee (in `fcr_up_price`). */
  feeMode?: boolean
}

export function ProfitHistoryChart(
  props: ProfitHistoryChartProps
): React.ReactElement {
  const { id, data, options, spinner, feeMode } = props
  const theme = useTheme()
  const { t } = useTranslation()
  const [main, setMain] = useState('')
  const [left, setLeft] = useState<string | JSX.Element>('')
  const [right, setRight] = useState('right')
  const hasPv = Profits.hasPvValue(data)

  const refreshSummary = (chart: Chart): void => {
    const { min, max } = chart.scales.x
    const idx = Math.floor((min + max) / 2)

    if (!data[idx]) return

    const {
      timestamp,
      fcr_up_price,
      fcr_down_price,
      fcr_up_power,
      fcr_down_power,
      batt_power,
      spot_saving,
      pv_value
    } = data[idx]

    setMain(lang.money(Profits.total(data[idx]), 'EUR')) // TODO: Correct currency x 3.
    const reserveInfo = feeMode
      ? <Box sx={{ pl: 4 }}>
          {t('RESERVE_AFTER_FEE')}
          <br />
          {lang.money(fcr_up_price, 'EUR')}
        </Box>
      : <>
          <Box sx={{ pl: 4 }}>
            {t('FCR_UP')}
            <br />
            {lang.num(fcr_up_power / 1000, 1, 'kW')}
            &nbsp;&nbsp;
            {lang.money(fcr_up_price, 'EUR')}
          </Box>
          <Box sx={{ pl: 4 }}>
            {t('FCR_DOWN')}
            <br />
            {lang.num(fcr_down_power / 1000, 1, 'kW')}
            &nbsp;&nbsp;
            {lang.money(fcr_down_price, 'EUR')}
          </Box>
        </>

    const info = (
      <Stack direction="row" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, rowGap: 1 }}>
        {reserveInfo}
        <Box sx={{ pl: 4 }}>
          {t('BATT_SAVINGS')}
          <br />
          {lang.num(batt_power / 1000, 1, 'kW')}
          &nbsp;&nbsp;
          {lang.money(spot_saving, 'EUR')}
        </Box>
        {hasPv && (
          <Box sx={{ pl: 4 }}>
            {t('PV_VALUE')}
            <br />
            {lang.money(pv_value ?? 0, 'EUR')}
          </Box>
        )}
      </Stack>
    )

    setLeft(info)

    if (timestamp.startsWith('1970')) {
      setRight('')
    } else {
      setRight(
        lang.timeLabel(
          DateTime.fromISO(timestamp).toMillis(),
          options.steps || 'hour'
        )
      )
    }
  }

  useEffect(() => {
    const yMax = maxOf(
      data.map((e) =>
        Math.ceil(
          e.fcr_down_price +
            e.fcr_up_price +
            (e.spot_saving > 0 ? e.spot_saving : 0.0) +
            (e.pv_value ?? 0)
        )
      )
    )
    const yMin = minOf(
      data.map((e) =>
        Math.floor(e.fcr_down_price + e.fcr_up_price + e.spot_saving)
      )
    )

    // Brand palette (graphic identity v4): the battery's own savings are violet,
    // solar is yellow, and the reserve is mint (up) and teal (down).
    const series: Array<{ label: string, color: string, values: number[] }> = [
      ...(feeMode
        ? [{ label: t('RESERVE_AFTER_FEE'), color: '#3FC1A5', values: data.map((e) => e.fcr_up_price) }]
        : [
            { label: t('FCR_UP'), color: '#3FC1A5', values: data.map((e) => e.fcr_up_price) },
            { label: t('FCR_DOWN'), color: '#1E7F7A', values: data.map((e) => e.fcr_down_price) }
          ]),
      { label: t('BATT_SAVINGS'), color: '#7E4AEB', values: data.map((e) => e.spot_saving) }
    ]

    // The solar value stacks on top of the others, and only exists when the
    // location has solar production: no bar, no legend entry otherwise.
    if (hasPv) {
      series.push({ label: t('PV_VALUE'), color: '#F4E356', values: data.map((e) => e.pv_value ?? 0) })
    }

    const values = series.map((s) => s.values)
    const isTop = (ctx: ScriptableContext<'bar'>): boolean => {
      const visible = series.map((_, k) => ctx.chart.isDatasetVisible(k))
      return ProfitBars.topIndex(values, visible, ctx.dataIndex) === ctx.datasetIndex
    }
    const inFocus = (ctx: ScriptableContext<'bar'>): boolean => {
      const { min, max } = ctx.chart.scales.x
      return ProfitBars.focusIndex(min, max) === ctx.dataIndex
    }

    const datasets = series.map(({ label, color, values }) => ({
      label,
      data: values,
      stack: 'combined',
      categoryPercentage: 0.7,
      barPercentage: 0.9,
      backgroundColor: (ctx: ScriptableContext<'bar'>) =>
        inFocus(ctx) ? color : ProfitBars.fade(color, ProfitBars.FADED),
      borderWidth: 0,
      borderSkipped: false as const,
      borderRadius: (ctx: ScriptableContext<'bar'>) =>
        isTop(ctx) ? { topLeft: ProfitBars.RADIUS, topRight: ProfitBars.RADIUS, bottomLeft: 0, bottomRight: 0 } : 0
    }))

    const config: ChartConfiguration = {
      type: 'bar',

      data: {
        labels: Series.labels(
          data.map((e) => DateTime.fromISO(e.timestamp).toMillis()),
          options
        ),
        datasets
      },

      plugins: [
        logoPlugin,
        {
          id: 'refreshSummary',
          afterRender: (chart) => refreshSummary(chart)
        }
      ],

      options: {
        animation: false,
        layout: {
          padding: { top: LOGO_PADDING }
        },
        plugins: {
          tooltip: {
            enabled: false
          },
          legend: {
            display: true,
            position: 'top',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 8,
              boxHeight: 8,
              padding: 16,
              // The bar colours are scriptable (faded out of focus): the legend
              // always shows the full colour.
              generateLabels: (chart) =>
                Chart.defaults.plugins.legend.labels.generateLabels(chart).map((item, k) => ({
                  ...item,
                  fillStyle: series[k].color,
                  strokeStyle: series[k].color,
                  lineWidth: 0
                }))
            }
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x',
              onPanComplete: ({ chart }) => refreshSummary(chart)
            }
          }
        },

        scales: {
          x: {
            min: Math.max(
              0,
              data.length - PROFIT_BAR_COUNT - Math.floor(PROFIT_BAR_COUNT / 2)
            ),
            max: Math.max(1, data.length - Math.ceil(PROFIT_BAR_COUNT / 2)),
            grid: {
              display: false
            },
            ticks: {
              color: theme.palette.grey[400],
              maxRotation: 0,
              font: {
                size: spinner ? 0 : 10
              }
            }
          },

          y: {
            min: yMin,
            max: yMax,
            border: {
              display: false
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.06)'
            },
            ticks: {
              color: theme.palette.grey[400],
              minRotation: 0,
              maxTicksLimit: 5,
              callback: (value) => sprintf('%.2f', value as number) + '€' // TODO: Find correct currency.
            }
          }
        },

        onClick: () => {
          props.onClick && props.onClick()
        }
      }
    }

    let currentChart = Chart.getChart(id)

    if (currentChart) {
      currentChart.destroy()
    }

    currentChart = new Chart(
      document.getElementById(id) as HTMLCanvasElement,
      config as ChartConfiguration
    )
    drawLogoWhenLoaded(currentChart)

    return () => {
      if (currentChart) {
        currentChart.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, data, options, t, theme.palette.grey, props, feeMode])

  return (
    <Box>
      <ChartSummary
        sx={{ mb: 2 }}
        main={main}
        steps={options.steps}
        unit=""
        left={left}
        right={right}
      />
      <Box
        sx={{
          display: spinner ? 'relative' : 'none',
          height: 0,
          pl: 4
        }}
      >
        <Box className="rotating-slow" sx={{ display: 'inline-block' }}>
          <ScatterPlot sx={{ fontSize: 100 }} />
        </Box>
      </Box>
      <canvas id={id} data-cy={`canvas-${id}`} />
    </Box>
  )
}
